import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson } from "./http/httpClient";
import { readArray, readBoolean, readNullableString, readObject, readString, type JsonObject } from "./http/jsonDecoding";
import { decisionOn, readAccessDecisions, type AccessDecisions, type AccessRefusal, type Decision } from "./access/decisions";
import type { PermittedAction } from "./permittedActions";

/**
 * Projects (module A): the ones the user can see, one project with what the user may do in
 * it, and creating one (where the session permits `create_project`). Corpora and all content
 * belong to a project.
 */

export interface ProjectSummary {
  readonly projectId: string;
  readonly name: string;
  /** The installation's own project "DMW": the read-only core every project inherits as its
   * DMW basis (ADR-0037 D5, F62). */
  readonly installationOwned: boolean;
  /** The DMW basis it inherits, as its pins say (F63, H-09); `null` for the DMW project, a
   * project that pins none, and outside the projects list. */
  readonly basis: ProjectBasis | null;
}

export interface ProjectBasis {
  /** The pinned DMW library version, `<collection id>@<commit>`. */
  readonly libraryVersion: string;
  /** The DMW process ontology release it holds, such as `1.0.0`; `null` for an older version. */
  readonly release: string | null;
  /** The change set that offers the newer DMW library, if one waits. */
  readonly updateChangeSetId: string | null;
}

export interface ProjectOverview {
  readonly project: ProjectSummary;
  /** Each project action: allowed, or refused with why and what would open it (SOT-02). */
  readonly decisions: AccessDecisions;
}

/** The overview's decision on `action`, asked for in its project. */
export function overviewDecision(overview: ProjectOverview, action: PermittedAction): Decision<AccessRefusal> {
  return decisionOn(overview.decisions, action, overview.project.projectId);
}

export function listProjects(signal?: AbortSignal): Promise<readonly ProjectSummary[]> {
  return requestJson({
    method: "GET",
    path: "/projects",
    signal,
    decodeResponse: (body) => readArray(body, "projects", (item) => decodeProject(readObject(item, "project"))),
  });
}

export function readProject(projectId: string, signal?: AbortSignal): Promise<ProjectOverview> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}`,
    signal,
    decodeResponse: (body) => {
      const overview = readObject(body, "project overview");
      const project = decodeProject(readObject(overview["project"], "project"));
      return { project, decisions: readAccessDecisions(overview["decisions"], project.projectId) };
    },
  });
}

/** A new project; it starts pinning the DMW library, with its starters (ADR-0037 D2). */
export function createProject(name: string): Promise<ProjectSummary> {
  const body = { name, start_with_dmw_library: true } satisfies ApiSchema<"ProjectCreationBody">;
  return requestJson({
    method: "POST",
    path: "/projects",
    body: jsonBody(body),
    decodeResponse: (response) => decodeProject(readObject(response, "project")),
  });
}

function decodeProject(project: JsonObject): ProjectSummary {
  const basis = project["basis"];
  return {
    projectId: readString(project, "id"),
    name: readString(project, "name"),
    installationOwned: readBoolean(project, "installation_owned"),
    basis: basis === null || basis === undefined ? null : decodeBasis(readObject(basis, "project basis")),
  };
}

function decodeBasis(basis: JsonObject): ProjectBasis {
  return {
    libraryVersion: readString(basis, "library_version"),
    release: readNullableString(basis, "release"),
    updateChangeSetId: readNullableString(basis, "update_change_set_id"),
  };
}

export type ProjectRole = ApiSchema<"ProjectRole">;

export const PROJECT_ROLES = enumMembers<ProjectRole>({ reader: true, contributor: true, project_administrator: true });
